import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Send,
  ContentCopy,
  Close,
  AccountBalanceWallet,
  ArrowForward,
  Info
} from '@mui/icons-material';
import { ethers } from 'ethers';

const SendDialog = ({ open, onClose, wallet, onSend }) => {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [token, setToken] = useState('ETH');
  const [gasPrice, setGasPrice] = useState('medium');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Form, 2: Confirmation, 3: Success

  // Available tokens based on the wallet's network
  const availableTokens = wallet?.network?.includes('Ethereum') 
    ? ['ETH', 'USDT', 'USDC', 'DAI'] 
    : wallet?.network?.includes('Solana')
      ? ['SOL', 'USDC', 'RAY', 'SRM']
      : wallet?.network?.includes('TON')
        ? ['TON', 'JETTON']
        : ['ETH'];

  // Gas price options
  const gasPriceOptions = {
    slow: { label: 'Slow (Cheaper)', value: '5' },
    medium: { label: 'Medium', value: '10' },
    fast: { label: 'Fast', value: '20' }
  };

  // Reset form
  const resetForm = () => {
    setRecipient('');
    setAmount('');
    setToken('ETH');
    setGasPrice('medium');
    setNote('');
    setError('');
    setStep(1);
  };

  // Handle close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Validate form
  const validateForm = () => {
    if (!recipient) {
      setError('Recipient address is required');
      return false;
    }

    if (!ethers.utils.isAddress(recipient) && !recipient.startsWith('UQ') && !recipient.startsWith('0x')) {
      setError('Invalid recipient address');
      return false;
    }

    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    // Check if amount is greater than balance
    const balance = parseFloat(wallet?.balance?.split(' ')[0] || '0');
    if (parseFloat(amount) > balance) {
      setError(`Insufficient balance. You have ${balance} ${token}`);
      return false;
    }

    setError('');
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (validateForm()) {
      setStep(2);
    }
  };

  // Handle send
  const handleSend = async () => {
    try {
      setLoading(true);
      setError('');

      // Simulate transaction
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Call the onSend callback with transaction details
      onSend({
        from: wallet.address,
        to: recipient,
        amount,
        token,
        gasPrice: gasPriceOptions[gasPrice].value,
        note,
        timestamp: new Date().toISOString()
      });

      setStep(3);
      setLoading(false);
    } catch (error) {
      console.error('Error sending transaction:', error);
      setError(error.message || 'Failed to send transaction');
      setLoading(false);
    }
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    if (address.length < 15) return address;
    return `${address.substring(0, 8)}...${address.substring(address.length - 6)}`;
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          boxShadow: 10
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Send />
          </Avatar>
          <Typography variant="h6">
            {step === 1 ? 'Send Funds' : step === 2 ? 'Confirm Transaction' : 'Transaction Sent'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {step === 1 && (
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(0, 163, 255, 0.1)', color: 'primary.main', mr: 1.5 }}>
                  <AccountBalanceWallet />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">From</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {wallet?.name || 'Your Wallet'} ({formatAddress(wallet?.address)})
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 'auto' }}>
                  {wallet?.balance || '0 ETH'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Recipient Address"
                fullWidth
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="Enter wallet address"
                variant="outlined"
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => {
                          navigator.clipboard.readText().then(
                            text => setRecipient(text),
                            err => console.error('Failed to read clipboard:', err)
                          );
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={8}>
              <TextField
                label="Amount"
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                type="number"
                placeholder="0.00"
                variant="outlined"
                margin="normal"
                InputProps={{
                  inputProps: { min: 0, step: 0.000001 }
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Token</InputLabel>
                <Select
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  label="Token"
                >
                  {availableTokens.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                Transaction Fee
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={gasPrice}
                  onChange={(e) => setGasPrice(e.target.value)}
                  displayEmpty
                  variant="outlined"
                >
                  {Object.entries(gasPriceOptions).map(([key, option]) => (
                    <MenuItem key={key} value={key}>
                      {option.label} ({option.value} Gwei)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Note (Optional)"
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note to this transaction"
                variant="outlined"
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        )}

        {step === 2 && (
          <Grid container spacing={2}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Please review the transaction details before confirming.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">From</Typography>
                    <Typography variant="body1">{formatAddress(wallet?.address)}</Typography>
                  </Box>
                  <ArrowForward color="action" />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">To</Typography>
                    <Typography variant="body1">{formatAddress(recipient)}</Typography>
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" fontWeight="medium">{amount} {token}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Network Fee</Typography>
                  <Typography variant="body1">{gasPriceOptions[gasPrice].value} Gwei</Typography>
                </Box>

                {note && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Note</Typography>
                    <Typography variant="body1" sx={{ maxWidth: '70%', wordBreak: 'break-word' }}>{note}</Typography>
                  </Box>
                )}

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">Total</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">{amount} {token}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}

        {step === 3 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
              <Send sx={{ fontSize: 30 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom>Transaction Sent Successfully!</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your transaction of {amount} {token} has been sent to {formatAddress(recipient)}.
            </Typography>
            <Alert severity="info" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
              <Typography variant="body2">
                Transaction ID: {Math.random().toString(36).substring(2, 15)}
              </Typography>
              <Typography variant="body2">
                Estimated confirmation time: 1-2 minutes
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 1 && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleNext}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            >
              Review
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Button onClick={() => setStep(1)} disabled={loading}>
              Back
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSend}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            >
              {loading ? 'Sending...' : 'Confirm & Send'}
            </Button>
          </>
        )}

        {step === 3 && (
          <Button variant="contained" onClick={handleClose} fullWidth>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendDialog;
